import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
    Toast,
    ToastContainer,
} from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/performance-review.css';

const emptyPhase = (id) => ({
    id,
    label: '',
    weightPercent: '',
    metrics: [{ name: '', target: '' }],
});

const snapshotToEditorPhases = (snapshot = []) =>
    snapshot.map((phase, index) => ({
        id: phase.id != null ? phase.id : index + 1,
        key: phase.key,
        label: phase.label || '',
        weightPercent: Math.round(Number(phase.weight) * 10000) / 100,
        metrics: (phase.metrics || []).map((m) => ({
            metric_key: m.metric_key,
            name: m.name || '',
            target: m.target || '',
        })),
    }));

const STATUS_VARIANT = {
    draft: 'secondary',
    active: 'success',
    archived: 'dark',
};

/** @param {string|null|undefined} status */
const normalizeStatus = (status) => String(status || 'draft').trim().toLowerCase();

/** Client-side list filters (no extra API calls when switching) */
const LIST_MODES = {
    current: { label: 'Active & drafts' },
    all: { label: 'All' },
    archived: { label: 'Archived only' },
};

const PerformanceReviewTemplates = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();
    const headers = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

    const [templates, setTemplates] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [error, setError] = useState('');
const [toastError, setToastError] = useState('');
    const [message, setMessage] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    // current | all | archived — client-side filter only
    const [listMode, setListMode] = useState('current');

    const [templateName, setTemplateName] = useState('');
    const [description, setDescription] = useState('');
    const [templateStatus, setTemplateStatus] = useState('draft');
    const [phases, setPhases] = useState([emptyPhase(1)]);

    const [confirmAction, setConfirmAction] = useState(null);

    const weightTotal = useMemo(
        () => phases.reduce((sum, p) => sum + (Number(p.weightPercent) || 0), 0),
        [phases]
    );
    const weightValid = Math.abs(weightTotal - 100) < 0.05;

    const normalizeTemplates = (raw) => {
        const list = Array.isArray(raw) ? raw : [];
        return list.map((t) => ({
            ...t,
            status: normalizeStatus(t.status),
        }));
    };

    // Single fetch of all templates (including archived); filtering is local.
    const load = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        setError('');
        try {
            const [tplRes, catalogRes] = await Promise.all([
                axios.get(`${config.apiBASEURL}/performance-review/templates`, {
                    headers,
                    params: { status: 'all' },
                }),
                axios.get(`${config.apiBASEURL}/performance-review/metrics/catalog`, { headers }),
            ]);
            setTemplates(normalizeTemplates(tplRes.data));
            setCatalog(catalogRes.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load templates');
        } finally {
            setLoading(false);
        }
    }, [accessToken, headers]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (catalog?.default_phases_snapshot?.length && !showForm) {
            setPhases(snapshotToEditorPhases(catalog.default_phases_snapshot));
        }
    }, [catalog, showForm]);

    /** Apply status change locally so filters update without waiting on a full reload. */
    const patchTemplateStatusLocal = (templateId, nextStatus) => {
        setTemplates((prev) =>
            prev.map((t) =>
                Number(t.template_id) === Number(templateId)
                    ? { ...t, status: normalizeStatus(nextStatus) }
                    : t
            )
        );
    };

    const removeTemplateLocal = (templateId) => {
        setTemplates((prev) => prev.filter((t) => Number(t.template_id) !== Number(templateId)));
    };

    const updatePhase = (phaseIndex, patch) => {
        setPhases((prev) => prev.map((p, i) => (i === phaseIndex ? { ...p, ...patch } : p)));
    };

    const updatePhaseMetric = (phaseIndex, metricIndex, patch) => {
        setPhases((prev) =>
            prev.map((p, i) => {
                if (i !== phaseIndex) return p;
                return {
                    ...p,
                    metrics: p.metrics.map((m, mi) => (mi === metricIndex ? { ...m, ...patch } : m)),
                };
            })
        );
    };

    const addPhase = () => setPhases((prev) => [...prev, emptyPhase(prev.length + 1)]);
    const removePhase = (phaseIndex) => {
        setPhases((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== phaseIndex).map((p, i) => ({ ...p, id: i + 1 }));
        });
    };
    const addMetric = (phaseIndex) => {
        setPhases((prev) =>
            prev.map((p, i) =>
                i === phaseIndex ? { ...p, metrics: [...p.metrics, { name: '', target: '' }] } : p
            )
        );
    };
    const removeMetric = (phaseIndex, metricIndex) => {
        setPhases((prev) =>
            prev.map((p, i) => {
                if (i !== phaseIndex) return p;
                if (p.metrics.length <= 1) return p;
                return { ...p, metrics: p.metrics.filter((_, mi) => mi !== metricIndex) };
            })
        );
    };

    const loadDefaultPhases = () => {
        if (catalog?.default_phases_snapshot?.length) {
            setPhases(snapshotToEditorPhases(catalog.default_phases_snapshot));
        }
    };

    const resetForm = () => {
        setTemplateName('');
        setDescription('');
        setTemplateStatus('draft');
        setEditingId(null);
        setPhases(
            catalog?.default_phases_snapshot?.length
                ? snapshotToEditorPhases(catalog.default_phases_snapshot)
                : [emptyPhase(1)]
        );
        setShowForm(false);
    };

    const openCreate = () => {
        setEditingId(null);
        setTemplateName('');
        setDescription('');
        setTemplateStatus('draft');
        setPhases(
            catalog?.default_phases_snapshot?.length
                ? snapshotToEditorPhases(catalog.default_phases_snapshot)
                : [emptyPhase(1)]
        );
        setShowForm(true);
        setToastError('');
        setMessage('');
    };
    
    const openEdit = (tpl) => {
        const st = normalizeStatus(tpl.status);
        if (st === 'archived') {
            setToastError('Archived templates cannot be edited. Restore them first.');
            return;
        }
        setEditingId(tpl.template_id);
        setTemplateName(tpl.name || '');
        setDescription(tpl.description || '');
        setTemplateStatus(st === 'active' ? 'active' : 'draft');
        setPhases(snapshotToEditorPhases(tpl.phases_snapshot || tpl.phases || []));
        setShowForm(true);
        setToastError('');
        setMessage('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const validateForm = () => {
        if (!templateName.trim()) {
            setToastError('Template name is required.');
            return false;
        }
        if (!weightValid) {
            setToastError(`Phase weightages must total 100%. Current total: ${Math.round(weightTotal * 100) / 100}%.`);
            return false;
        }
        for (const phase of phases) {
            if (!phase.label.trim()) {
                setToastError('Every phase needs a label.');
                return false;
            }
            if (!phase.metrics.every((m) => m.name.trim())) {
                setToastError(`Every metric in phase "${phase.label}" needs a name.`);
                return false;
            }
        }
        return true;
    };

    const buildPayload = () => ({
        name: templateName.trim(),
        description: description.trim() || null,
        status: templateStatus === 'active' ? 'active' : 'draft',
        phases: phases.map((p) => ({
            id: p.id,
            key: p.key,
            label: p.label.trim(),
            weight: Number(p.weightPercent),
            metrics: p.metrics.map((m) => ({
                metric_key: m.metric_key,
                name: m.name.trim(),
                target: m.target || '',
            })),
        })),
    });

    const handleSave = async () => {
        if (!validateForm()) return;
    
        setSaving(true);
        setToastError('');
        setMessage('');
        try {
            if (editingId) {
                const { data } = await axios.put(
                    `${config.apiBASEURL}/performance-review/templates/${editingId}`,
                    buildPayload(),
                    { headers }
                );
                const resynced = Number(data?.resynced_count) || 0;
                setMessage(
                    resynced > 0
                        ? `Template updated. ${resynced} unfilled assigned review${resynced === 1 ? '' : 's'} synced to the new structure.`
                        : 'Template updated. Assigned reviews that were already filled keep their original structure.'
                );
            } else {
                await axios.post(
                    `${config.apiBASEURL}/performance-review/templates`,
                    buildPayload(),
                    { headers }
                );
                setMessage('Template saved as draft. Activate it when ready to assign.');
            }
            resetForm();
            await load();
        } catch (err) {
            setToastError(err.response?.data?.message || 'Could not save template');
        } finally {
            setSaving(false);
        }
    };

    const runConfirmedAction = async () => {
        if (!confirmAction || actionBusy) return;
        const { type, template } = confirmAction;
        setConfirmAction(null);
        setError('');
        setMessage('');
        setActionBusy(true);
        try {
            if (type === 'activate') {
                await axios.patch(
                    `${config.apiBASEURL}/performance-review/templates/${template.template_id}/status`,
                    { status: 'active' },
                    { headers }
                );
                patchTemplateStatusLocal(template.template_id, 'active');
                setMessage(`"${template.name}" is now active and can be assigned.`);
            } else if (type === 'deactivate') {
                await axios.patch(
                    `${config.apiBASEURL}/performance-review/templates/${template.template_id}/status`,
                    { status: 'draft' },
                    { headers }
                );
                patchTemplateStatusLocal(template.template_id, 'draft');
                setMessage(`"${template.name}" set to draft (not assignable).`);
            } else if (type === 'archive') {
                await axios.patch(
                    `${config.apiBASEURL}/performance-review/templates/${template.template_id}/status`,
                    { status: 'archived' },
                    { headers }
                );
                patchTemplateStatusLocal(template.template_id, 'archived');
                setMessage(`"${template.name}" archived.`);
                if (editingId === template.template_id) resetForm();
                setListMode('archived');
            } else if (type === 'restore') {
                await axios.patch(
                    `${config.apiBASEURL}/performance-review/templates/${template.template_id}/status`,
                    { status: 'draft' },
                    { headers }
                );
                patchTemplateStatusLocal(template.template_id, 'draft');
                setMessage(`"${template.name}" restored as draft.`);
                setListMode('current');
            } else if (type === 'hardDelete') {
                await axios.delete(
                    `${config.apiBASEURL}/performance-review/templates/${template.template_id}`,
                    { headers, params: { hard: true } }
                );
                removeTemplateLocal(template.template_id);
                setMessage(`"${template.name}" permanently deleted.`);
                if (editingId === template.template_id) resetForm();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Action failed');
            // Resync if the optimistic path failed mid-way
            await load();
        } finally {
            setActionBusy(false);
        }
    };

    const visibleTemplates = useMemo(() => {
        if (listMode === 'all') return templates;
        if (listMode === 'archived') {
            return templates.filter((t) => normalizeStatus(t.status) === 'archived');
        }
        // current: draft + active
        return templates.filter((t) => {
            const st = normalizeStatus(t.status);
            return st === 'draft' || st === 'active';
        });
    }, [templates, listMode]);

    const confirmCopy = useMemo(() => {
        if (!confirmAction) return { title: '', body: '' };
        const name = confirmAction.template?.name || 'this template';
        switch (confirmAction.type) {
            case 'activate':
                return {
                    title: 'Activate template?',
                    body: `Activate "${name}" so it can be assigned to employees? Draft/inactive templates cannot be assigned.`,
                };
            case 'deactivate':
                return {
                    title: 'Set template to draft?',
                    body: `Set "${name}" back to draft? It will no longer appear when assigning reviews.`,
                };
            case 'archive':
                return {
                    title: 'Archive template?',
                    body: `Archive "${name}"? Filled and open reviews keep their current structure; the template will be hidden from assign lists.`,
                };
            case 'restore':
                return {
                    title: 'Restore template?',
                    body: `Restore "${name}" as a draft so you can edit and activate it again?`,
                };
            case 'hardDelete':
                return {
                    title: 'Permanently delete?',
                    body: `Permanently delete "${name}"? This only works if it was never assigned. This cannot be undone.`,
                };
            default:
                return { title: 'Confirm', body: 'Continue?' };
        }
    }, [confirmAction]);

    return (
        <div className="pr-list-page">
            <Row className="body_content g-3">
                <Col xs={12}>
                    <Breadcrumb className="mb-0">
                        <Breadcrumb.Item
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate('/Graphe/dashboard/employee_list')}
                        >
                            Employee
                        </Breadcrumb.Item>
                        <Breadcrumb.Item active>Review Templates</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>

                <Col xs={12}>
                    <div className="pr-list-header">
                        <div>
                            <h1 className="pr-page-title mb-1">Review Templates</h1>
                            <p className="pr-page-subtitle mb-0">
                                Save templates as draft, edit anytime, then activate before assigning. Weightages must total 100%.
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            className="pr-new-btn"
                            onClick={() => (showForm ? resetForm() : openCreate())}
                        >
                            {showForm ? 'Close form' : '+ New Template'}
                        </Button>
                    </div>
                </Col>

                {error && <Col xs={12}><Alert variant="danger">{error}</Alert></Col>}
                {message && <Col xs={12}><Alert variant="success">{message}</Alert></Col>}

                {showForm && (
                    <Col xs={12}>
                        <Card className="mb-3">
                            <Card.Body>
                                <h5 className="mb-3">{editingId ? 'Edit template' : 'New template'}</h5>
                                <Row className="g-3 mb-3">
                                    <Col md={5}>
                                        <Form.Label>Template name</Form.Label>
                                        <Form.Control
                                            value={templateName}
                                            onChange={(e) => setTemplateName(e.target.value)}
                                            placeholder="e.g. Q3 Self-Review Template"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Description (optional)</Form.Label>
                                        <Form.Control
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Short description"
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            value={templateStatus}
                                            onChange={(e) => setTemplateStatus(e.target.value)}
                                        >
                                            <option value="draft">Draft (not assignable)</option>
                                            <option value="active">Active (assignable)</option>
                                        </Form.Select>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                                    <div>
                                        <h6 className="mb-1">Review phases</h6>
                                        <p className="text-muted small mb-0">
                                            Combined weightage must total 100%.
                                        </p>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button size="sm" variant="outline-secondary" onClick={loadDefaultPhases}>
                                            Load defaults
                                        </Button>
                                        <Button size="sm" variant="outline-primary" onClick={addPhase}>
                                            + Add phase
                                        </Button>
                                    </div>
                                </div>

                                <Alert variant={weightValid ? 'success' : 'warning'} className="py-2">
                                    Total weightage: <strong>{Math.round(weightTotal * 100) / 100}%</strong>
                                    {weightValid ? ' — ready' : ' — must equal 100%'}
                                </Alert>

                                {phases.map((phase, phaseIndex) => (
                                    <div key={`phase-${phaseIndex}`} className="pr-phase-card">
                                        <div className="pr-phase-header">
                                            <div className="flex-grow-1 me-3">
                                                <Form.Label className="small mb-1">Phase {phaseIndex + 1} label</Form.Label>
                                                <Form.Control
                                                    value={phase.label}
                                                    onChange={(e) => updatePhase(phaseIndex, { label: e.target.value })}
                                                    placeholder="Phase name"
                                                />
                                            </div>
                                            <div style={{ width: 120 }}>
                                                <Form.Label className="small mb-1">Weight %</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    value={phase.weightPercent}
                                                    onChange={(e) =>
                                                        updatePhase(phaseIndex, { weightPercent: e.target.value })
                                                    }
                                                />
                                            </div>
                                            {phases.length > 1 && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    className="ms-2 align-self-end"
                                                    onClick={() => removePhase(phaseIndex)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                        <div className="mt-3 px-3 pb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong className="small">Metrics</strong>
                                                <Button size="sm" variant="link" className="p-0" onClick={() => addMetric(phaseIndex)}>
                                                    + Add metric
                                                </Button>
                                            </div>
                                            {phase.metrics.map((metric, metricIndex) => (
                                                <Row key={`m-${phaseIndex}-${metricIndex}`} className="g-2 mb-2">
                                                    <Col md={5}>
                                                        <Form.Control
                                                            placeholder="Metric name"
                                                            value={metric.name}
                                                            onChange={(e) =>
                                                                updatePhaseMetric(phaseIndex, metricIndex, {
                                                                    name: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Control
                                                            placeholder="Target / guideline"
                                                            value={metric.target}
                                                            onChange={(e) =>
                                                                updatePhaseMetric(phaseIndex, metricIndex, {
                                                                    target: e.target.value,
                                                                })
                                                            }
                                                        />
                                                    </Col>
                                                    <Col md={2}>
                                                        {phase.metrics.length > 1 && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                onClick={() => removeMetric(phaseIndex, metricIndex)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Row>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                <div className="d-flex gap-2 mt-3">
                                    <Button variant="primary" onClick={handleSave} disabled={saving || !weightValid}>
                                        {saving
                                            ? 'Saving…'
                                            : editingId
                                                ? 'Update template'
                                                : 'Save draft'}
                                    </Button>
                                    <Button variant="outline-secondary" onClick={resetForm} disabled={saving}>
                                        Cancel
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                <Col xs={12}>
                    <Card className="pr-list-card">
                        <Card.Header className="pr-list-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <strong>Existing templates</strong>
                                <span className="pr-result-count ms-2">{visibleTemplates.length} template(s)</span>
                            </div>
                            <div className="d-flex flex-wrap gap-1 align-items-center" role="group" aria-label="Template list filter">
                                {Object.entries(LIST_MODES).map(([key, cfg]) => (
                                    <Button
                                        key={key}
                                        size="sm"
                                        variant={listMode === key ? 'secondary' : 'outline-secondary'}
                                        disabled={actionBusy}
                                        onClick={() => setListMode(key)}
                                    >
                                        {cfg.label}
                                    </Button>
                                ))}
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loading && visibleTemplates.length === 0 && (
                                <div className="pr-empty-state">
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Loading templates…
                                </div>
                            )}
                            {!loading && visibleTemplates.length === 0 && (
                                <div className="pr-empty-state">
                                    <h6 className="mb-2">
                                        {listMode === 'archived'
                                            ? 'No archived templates'
                                            : listMode === 'all'
                                                ? 'No templates found'
                                                : 'No templates yet'}
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        {listMode === 'archived'
                                            ? 'Archive a template to see it here. Use Restore to bring it back as a draft.'
                                            : 'Create your first template to assign later.'}
                                    </p>
                                </div>
                            )}
                            {visibleTemplates.length > 0 && (
                                <div className="table-responsive">
                                    {loading && (
                                        <div className="px-3 py-2 small text-muted border-bottom">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Refreshing…
                                        </div>
                                    )}
                                    <Table hover className="pr-table mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Phases</th>
                                                <th>Created</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleTemplates.map((tpl) => {
                                                const st = normalizeStatus(tpl.status);
                                                const isArchived = st === 'archived';
                                                return (
                                                <tr
                                                    key={tpl.template_id}
                                                    className={isArchived ? 'pr-row-archived' : undefined}
                                                >
                                                    <td>
                                                        <strong>{tpl.name}</strong>
                                                        {tpl.description && (
                                                            <div className="text-muted small">{tpl.description}</div>
                                                        )}
                                                    </td>
                                                    <td className="small">
                                                        {(tpl.phases || []).map((p) => {
                                                            const raw = Number(p.weight);
                                                            const percent = raw > 1 ? raw : raw * 100;
                                                            return (
                                                                <div key={p.phase_id || p.id}>
                                                                    {p.label} ({Math.round(percent)}%)
                                                                </div>
                                                            );
                                                        })}
                                                    </td>
                                                    <td className="small text-nowrap">
                                                        {tpl.created_at
                                                            ? moment(tpl.created_at).format('DD MMM YYYY')
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            bg={STATUS_VARIANT[st] || 'secondary'}
                                                            className="pr-status-badge"
                                                        >
                                                            {st}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end text-nowrap">
                                                        {!isArchived && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                className="me-1 mb-1"
                                                                disabled={actionBusy}
                                                                onClick={() => openEdit(tpl)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        )}
                                                        {st === 'draft' && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-success"
                                                                className="me-1 mb-1"
                                                                disabled={actionBusy}
                                                                onClick={() =>
                                                                    setConfirmAction({ type: 'activate', template: tpl })
                                                                }
                                                            >
                                                                Activate
                                                            </Button>
                                                        )}
                                                        {st === 'active' && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                className="me-1 mb-1"
                                                                disabled={actionBusy}
                                                                onClick={() =>
                                                                    setConfirmAction({ type: 'deactivate', template: tpl })
                                                                }
                                                            >
                                                                Set draft
                                                            </Button>
                                                        )}
                                                        {isArchived ? (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-success"
                                                                className="me-1 mb-1"
                                                                disabled={actionBusy}
                                                                onClick={() =>
                                                                    setConfirmAction({ type: 'restore', template: tpl })
                                                                }
                                                            >
                                                                Restore
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                className="me-1 mb-1"
                                                                disabled={actionBusy}
                                                                onClick={() =>
                                                                    setConfirmAction({ type: 'archive', template: tpl })
                                                                }
                                                            >
                                                                Archive
                                                            </Button>
                                                        )}
                                                        {(st === 'draft' || isArchived) && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline-dark"
                                                                className="mb-1"
                                                                disabled={actionBusy}
                                                                onClick={() =>
                                                                    setConfirmAction({ type: 'hardDelete', template: tpl })
                                                                }
                                                            >
                                                                Delete
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmCopy.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmCopy.body}</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setConfirmAction(null)}>
                        Cancel
                    </Button>
                    <Button
                        variant={
                            confirmAction?.type === 'archive' || confirmAction?.type === 'hardDelete'
                                ? 'danger'
                                : 'primary'
                        }
                        disabled={actionBusy}
                        onClick={runConfirmedAction}
                    >
                        {actionBusy ? 'Working…' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <ToastContainer position="bottom-end" className="p-3" style={{ position: 'fixed', zIndex: 1060 }}>
                <Toast
                    show={!!toastError}
                    onClose={() => setToastError('')}
                    delay={5000}
                    autohide
                    bg="danger"
                >
                    <Toast.Header closeButton>
                        <strong className="me-auto">Something went wrong</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{toastError}</Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
};

export default PerformanceReviewTemplates;
